/**
 * Раскрытие строки виджета «Предписания карантина» — история ОДНОГО случая
 * (agroai-0e8, перенесено из бывшего блока «Предписания» экрана «Карантин»):
 *
 * * таймлайн этапов: находка → предписание → работы хозяйства (отчёт
 *   «обработали») → замер инспектора → решение / устранение (по замеру или
 *   акту службы). Отчёт хозяйства — не устранение: цвет меняет только замер;
 * * карточки деталей: инспектор, поражённая часть и её площадь, акты службы,
 *   документы (в базе пока нет — сказано словами), работа в агроцикле;
 * * «Полное дело контура» — все события контура и ввод инспектора/службы
 *   (`QuarantineEntry`) — тот же компонент, что у стола карантина.
 *
 * Всё приходит строкой `/quarantine/prescriptions` (сервер), полное дело
 * читается только по нажатию.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, ArrowRight, Check, MapPin, Sprout, Stamp, UserRound } from 'lucide-react';
import type { Schemas } from '../../../api/client';
import { useAnalysisContext } from '../../../analysis/context';
import { Disclosure } from '../../ui/Disclosure';
import { Icon } from '../../ui/Icon';
import { dateShort } from '../../lib/format';
import { CLASS_LONG, haText } from '../../pages/quarantine/quarantineArea';
import { QuarantineCase } from '../../pages/quarantine/QuarantineCase';
import { WorkCycleLink } from '../../pages/quarantine/PrescriptionsBlock';
import { FOLLOWUP_LABEL } from '../../pages/cabinet/model';
import { lazyWords } from '../../lib/lazyWords';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { STAGES, stepStates, type Stage } from './prescriptionsModel';
import { STAGE_ICON } from './caseIcons';
import '../../pages/quarantineWave2.css';

type Work = Schemas['QPrescriptionWorkOut'];

const m = defineMessages({
  stepsAria: { id: 'widget.prescriptions.case.stepsAria', defaultMessage: 'Этапы случая', description: 'aria-label таймлайна этапов случая карантина в раскрытой строке' },
  sFinding: { id: 'widget.prescriptions.case.step.finding', defaultMessage: 'Находка', description: 'Этап случая карантина: находка инспектора' },
  sRequirement: { id: 'widget.prescriptions.case.step.requirement', defaultMessage: 'Предписание', description: 'Этап случая карантина: предписание хозяйству' },
  sReport: { id: 'widget.prescriptions.case.step.report', defaultMessage: 'Работы хозяйства', description: 'Этап случая карантина: работы хозяйства и отчёт «обработали»' },
  sFollowup: { id: 'widget.prescriptions.case.step.followup', defaultMessage: 'Замер инспектора', description: 'Этап случая карантина: повторный (контрольный) замер инспектора' },
  sResolved: { id: 'widget.prescriptions.case.step.resolved', defaultMessage: 'Решение', description: 'Этап случая карантина: решение службы или устранение по замеру/акту' },
  noDate: { id: 'widget.prescriptions.case.noDate', defaultMessage: 'без даты', description: 'Вместо даты этапа, когда дата не названа; строчными' },
  pending: { id: 'widget.prescriptions.case.pending', defaultMessage: 'ещё нет', description: 'Этап случая ещё не наступил; строчными' },
  deadline: { id: 'widget.prescriptions.case.deadline', defaultMessage: 'срок {date}', description: 'Срок предписания под этапом «Предписание»; {date} — дата' },
  noDeadline: { id: 'widget.prescriptions.case.noDeadline', defaultMessage: 'срок не назван', description: 'Под этапом «Предписание»: срок не назван; строчными' },
  lateDays: { id: 'widget.prescriptions.case.lateDays', defaultMessage: '{days, plural, one {# день} few {# дня} many {# дней} other {# дня}} после срока', description: 'Сколько дней прошло после срока предписания (считает сервер); # — число дней' },
  reported: { id: 'widget.prescriptions.case.reported', defaultMessage: 'отчёт «обработали» — это не устранение', description: 'Под этапом «Работы хозяйства»: хозяйство отчиталось об обработке, устранение подтверждает только замер; строчными' },
  awaitMeasure: { id: 'widget.prescriptions.case.awaitMeasure', defaultMessage: 'ждём замера инспектора', description: 'Под этапом «Замер инспектора»: отчёт есть, замера ещё нет; строчными' },
  eliminated: { id: 'widget.prescriptions.case.eliminated', defaultMessage: 'устранено по замеру', description: 'Под этапом «Решение»: устранение подтверждено замером инспектора; строчными' },
  eliminatedAct: { id: 'widget.prescriptions.case.eliminatedAct', defaultMessage: 'устранено — снято автоматически по акту', description: 'Под этапом «Решение»: устранение пришло из загруженного акта службы, предписание закрылось само; строчными' },
  decSetRed: { id: 'widget.prescriptions.case.decision.setRed', defaultMessage: 'решение службы: красный', description: 'Под этапом «Решение»: ответственный поставил красный статус; строчными' },
  decLiftRed: { id: 'widget.prescriptions.case.decision.liftRed', defaultMessage: 'решение службы: красный снят', description: 'Под этапом «Решение»: ответственный снял красный статус; строчными' },
  decKeep: { id: 'widget.prescriptions.case.decision.keep', defaultMessage: 'решение службы: без красного', description: 'Под этапом «Решение»: ответственный оставил без красного; строчными' },
  dInspector: { id: 'widget.prescriptions.case.d.inspector', defaultMessage: 'Инспектор', description: 'Карточка деталей случая: инспектор обследования' },
  inspectedOn: { id: 'widget.prescriptions.case.d.inspectedOn', defaultMessage: 'обследование {date}', description: 'Дата обследования под именем инспектора; {date} — дата' },
  noData: { id: 'widget.prescriptions.case.noData', defaultMessage: 'нет данных: {items}', description: 'Одна тонкая строка вместо пустых карточек случая; {items} — перечень через запятую (инспектор, акты, документы)' },
  noDataInspector: { id: 'widget.prescriptions.case.noData.inspector', defaultMessage: 'инспектор', description: 'В строке «нет данных»: инспектор обследования не указан; строчными' },
  noDataActs: { id: 'widget.prescriptions.case.noData.acts', defaultMessage: 'акты', description: 'В строке «нет данных»: актов службы по случаю нет; строчными' },
  noDataDocs: { id: 'widget.prescriptions.case.noData.docs', defaultMessage: 'документы', description: 'В строке «нет данных»: документов и фото к случаю в базе нет; строчными' },
  overdueMark: { id: 'widget.prescriptions.case.overdueMark', defaultMessage: 'просрочено', description: 'Метка просроченного этапа на шкале случая; строчными' },
  stepDone: { id: 'widget.prescriptions.case.stepDone', defaultMessage: 'пройден', description: 'Скрытая подпись пройденного этапа шкалы случая; строчными' },
  dPart: { id: 'widget.prescriptions.case.d.part', defaultMessage: 'Поражённая часть', description: 'Карточка деталей случая: где на контуре поражение и какая площадь' },
  partNamed: { id: 'widget.prescriptions.case.d.partNamed', defaultMessage: 'часть «{part}»', description: 'Поражённая часть контура; {part} — название части' },
  partWhole: { id: 'widget.prescriptions.case.d.partWhole', defaultMessage: 'контур целиком', description: 'Поражение отнесено ко всему контуру; строчными' },
  partUnspecified: { id: 'widget.prescriptions.case.d.partUnspecified', defaultMessage: 'часть не указана — взята вся единица', description: 'Площадь поражённой части не указана, под сигналом вся обследованная единица; строчными' },
  areaUnknown: { id: 'widget.prescriptions.case.d.areaUnknown', defaultMessage: 'площадь не названа', description: 'Площадь поражённой части неизвестна; строчными' },
  dActs: { id: 'widget.prescriptions.case.d.acts', defaultMessage: 'Акты службы', description: 'Карточка деталей случая: загруженные акты карантинной службы' },
  actLine: { id: 'widget.prescriptions.case.d.actLine', defaultMessage: '{kind} № {no} от {date}', description: 'Строка акта службы; {kind} — вид акта, {no} — номер, {date} — дата' },
  actInspection: { id: 'widget.prescriptions.case.act.inspection', defaultMessage: 'Акт обследования', description: 'Вид акта службы: обследование' },
  actFinding: { id: 'widget.prescriptions.case.act.finding', defaultMessage: 'Акт находки', description: 'Вид акта службы: находка' },
  actRequirement: { id: 'widget.prescriptions.case.act.requirement', defaultMessage: 'Предписание', description: 'Вид акта службы: предписание' },
  actFollowup: { id: 'widget.prescriptions.case.act.followup', defaultMessage: 'Акт контрольного замера', description: 'Вид акта службы: контрольный замер' },
  actDecision: { id: 'widget.prescriptions.case.act.decision', defaultMessage: 'Решение', description: 'Вид акта службы: решение ответственного' },
  dWork: { id: 'widget.prescriptions.case.d.work', defaultMessage: 'Работа в агроцикле', description: 'Карточка деталей случая: работа хозяйства по предписанию в агроцикле' },
  workArea: { id: 'widget.prescriptions.case.d.workArea', defaultMessage: 'площадь работы {area}', description: 'Площадь работы по предписанию («га работ»); {area} — «3,2 га»' },
  toContour: { id: 'widget.prescriptions.case.toContour', defaultMessage: 'Карточка контура', description: 'Ссылка из раскрытой строки: карточка контура на вкладке карантина' },
  toFarm: { id: 'widget.prescriptions.case.toFarm', defaultMessage: 'Хозяйство {name}', description: 'Ссылка из раскрытой строки: кабинет хозяйства; {name} — название' },
  fullCase: { id: 'widget.prescriptions.case.full', defaultMessage: 'Полное дело контура', description: 'Раскрывающийся блок: все события карантина по контуру и ввод инспектора/службы' },
  fullCaseSummary: { id: 'widget.prescriptions.case.fullSummary', defaultMessage: 'все события по датам, ввод замера и решения', description: 'Подпись свёрнутого блока «Полное дело контура»; строчными' },
});

const STEP_LABEL: Record<Stage, keyof typeof m> = {
  finding: 'sFinding',
  requirement: 'sRequirement',
  report: 'sReport',
  followup: 'sFollowup',
  resolved: 'sResolved',
};

const ACT_WORD: Record<string, string> = lazyWords({
  inspection: m.actInspection,
  finding: m.actFinding,
  requirement: m.actRequirement,
  followup: m.actFollowup,
  decision: m.actDecision,
});

const DECISION_WORD: Record<string, string> = lazyWords({
  set_red: m.decSetRed,
  lift_red: m.decLiftRed,
  keep: m.decKeep,
});

const ha = (microha: number | null | undefined): string | null =>
  microha === null || microha === undefined ? null : haText(microha / 1_000_000);

/**
 * `onContour` — виджет стоит на самой карточке контура: ссылка «Карточка
 * контура» и «Полное дело» там лишние (дело и ввод инспектора — на той же
 * вкладке выше).
 */
export function PrescriptionCase({ work: w, workStatusWord, showFarm, onContour = false }: { work: Work; workStatusWord: string; showFarm: boolean; onContour?: boolean }) {
  const t = useT();
  const analysis = useAnalysisContext();
  const states = stepStates(w);
  const byAct = w.acts.some((a) => a.kind === 'followup');
  const contourHref = `/contours/${w.contour_id}${analysis.linkSearch({ tab: 'quarantine' })}`;

  const step: Record<Stage, { date: string | null; text: ReactNode }> = {
    finding: {
      date: w.found_on ?? null,
      text: [w.problem_name, w.object_class ? (CLASS_LONG as Record<string, string>)[w.object_class] ?? w.object_class : null].filter(Boolean).join(' · '),
    },
    requirement: {
      date: w.issued_on,
      text: (
        <>
          {w.deadline ? t(m.deadline, { date: dateShort(w.deadline) }) : t(m.noDeadline)}
          {w.overdue_days ? <span className="crx-late">{t(m.lateDays, { days: w.overdue_days })}</span> : null}
        </>
      ),
    },
    report: {
      date: w.reported_on ?? null,
      text: w.reported_on ? t(m.reported) : workStatusWord,
    },
    followup: {
      date: w.followup_on ?? null,
      text: w.followup_on
        ? FOLLOWUP_LABEL[w.followup_outcome ?? ''] ?? w.followup_outcome
        : w.reported_on && !w.eliminated_on ? t(m.awaitMeasure) : t(m.pending),
    },
    resolved: {
      date: w.eliminated_on ?? w.decision_on ?? null,
      text: w.eliminated_on
        ? t(byAct ? m.eliminatedAct : m.eliminated)
        : w.decision_kind
          ? DECISION_WORD[w.decision_kind] ?? w.decision_kind
          : t(m.pending),
    },
  };

  const area = ha(w.signal_microha);
  const workArea = ha(w.area_microha);

  return (
    <div className="crx-case">
      <ol className="crx-steps" aria-label={t(m.stepsAria)}>
        {STAGES.map((s) => (
          <li key={s} className="crx-step" data-state={states[s]}>
            <span className="crx-step__dot" aria-hidden="true">
              <Icon icon={STAGE_ICON[s]} size={16} />
              {states[s] === 'done' ? <span className="crx-step__check"><Check size={10} strokeWidth={3} /></span> : null}
            </span>
            <span className="crx-step__label">
              {t(m[STEP_LABEL[s]])}
              {states[s] === 'done' ? <span className="visually-hidden"> ({t(m.stepDone)})</span> : null}
            </span>
            <time className="crx-step__date num">
              {step[s].date ? dateShort(step[s].date!) : s === 'finding' || s === 'requirement' ? t(m.noDate) : '—'}
            </time>
            <span className="crx-step__text">{step[s].text}</span>
            {states[s] === 'late' ? (
              <span className="crx-late crx-late--step">
                <Icon icon={AlarmClock} size={16} />
                {w.overdue_days ? t(m.lateDays, { days: w.overdue_days }) : t(m.overdueMark)}
              </span>
            ) : null}
          </li>
        ))}
      </ol>

      <div className="crx-facts">
        {w.inspector ? (
          <Fact icon={UserRound} title={t(m.dInspector)}>
            <strong>{w.inspector}</strong>
            {w.found_on ? <span>{t(m.inspectedOn, { date: dateShort(w.found_on) })}</span> : null}
          </Fact>
        ) : null}
        <Fact icon={MapPin} title={t(m.dPart)}>
          <strong>{area ?? t(m.areaUnknown)}</strong>
          <span>
            {w.part_label ? t(m.partNamed, { part: w.part_label }) : t(m.partWhole)}
            {w.area_basis === 'whole_unspecified' ? ` · ${t(m.partUnspecified)}` : ''}
          </span>
        </Fact>
        {w.acts.length > 0 ? (
          <Fact icon={Stamp} title={t(m.dActs)}>
            <ul className="crx-fact__list">
              {w.acts.map((a) => (
                <li key={`${a.kind}-${a.no}-${a.act_date}`}>
                  {t(m.actLine, { kind: ACT_WORD[a.kind] ?? a.kind, no: a.no, date: dateShort(a.act_date) })}
                </li>
              ))}
            </ul>
          </Fact>
        ) : null}
        <Fact icon={Sprout} title={t(m.dWork)}>
          <strong><WorkCycleLink work={w} /></strong>
          <span>
            {workStatusWord}
            {workArea ? ` · ${t(m.workArea, { area: workArea })}` : ''}
          </span>
        </Fact>
      </div>
      {/* Документов и фото в базе нет вовсе; пустые карточки не рисуем — одна тонкая строка. */}
      <p className="crx-nodata">
        {t(m.noData, { items: [w.inspector ? null : t(m.noDataInspector), w.acts.length > 0 ? null : t(m.noDataActs), t(m.noDataDocs)].filter(Boolean).join(', ') })}
      </p>

      {onContour ? null : (
        <>
          <p className="crx-case__links">
            <Link className="crx-go" to={contourHref}>{t(m.toContour)} <Icon icon={ArrowRight} size={16} /></Link>
            {showFarm && w.actor_name ? (
              <Link className="crx-go" to={`/farms/${w.actor_id}${analysis.linkSearch()}`}>
                {t(m.toFarm, { name: w.actor_name })} <Icon icon={ArrowRight} size={16} />
              </Link>
            ) : null}
          </p>
          <Disclosure label={t(m.fullCase)} summary={t(m.fullCaseSummary)}>
            <QuarantineCase
              contourId={String(w.contour_id)}
              params={analysis.params}
              asOf={analysis.asOf}
              contourHref={contourHref}
            />
          </Disclosure>
        </>
      )}
    </div>
  );
}

function Fact({ icon, title, children }: { icon: typeof UserRound; title: string; children: ReactNode }) {
  return (
    <section className="crx-fact">
      <h4 className="crx-fact__title"><Icon icon={icon} size={16} />{title}</h4>
      <div className="crx-fact__body">{children}</div>
    </section>
  );
}
